<template>
  <p
    ref="element"
    data-test="contentEditableInput"
    class="content-editable-input"
    :contenteditable="contenteditable!"
    :class="[
      formattedTextClass,
      { 'is-placeholder-visible': showPlaceholder },
    ]"
    :data-placeholder="placeholder || undefined"
    :inputmode="inputMode"
    @beforeinput="onBeforeInput"
    @input="update"
    @blur="onBlur"
    @paste="onPaste"
    @drop="onDrop"
    @dragover="onDragOver"
    @keyup.up="onKeyUpIsUp"
    @keyup.esc="onKeyUpIsEsc"
  />
</template>

<script lang="ts" setup>
/* eslint-disable @typescript-eslint/no-non-null-assertion */

import { ref, onMounted, watch, computed } from 'vue'

import { placeCaretAtEnd, restoreContentEditableSelection, snapshotContentEditableSelection } from '@/shared/utils/contenteditable-selection'
import { normalizePasteHtml } from '@/shared/utils/normalize-paste-html'
import { sanitizeOutboundMessage } from '@/shared/utils/sanitize'

const props = withDefaults(defineProps<{
  contenteditable?: boolean
  modelValue: string
  noHTML?: boolean
  noNL?: boolean
  maxLength?: number | null,
  placeholder?: string | undefined
  inputMode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search' | undefined
}>(), {
  contenteditable: true,
  noHTML: true,
  noNL: false,
  maxLength: null,
  placeholder: undefined,
  inputMode: undefined,
})

const emit = defineEmits<{
  (event: 'returned', value: string): void;
  (event: 'update:modelValue', value: string): void;
  (event: 'blur', value: Event): void;
  (event: 'filePicked', value: FileList): void;
  (event: 'editMyPreviousMessage'): void;
  (event: 'cancelEditingMyPreviousMessage'): void;
}>()

function replaceAll (str: string, search: string, replacement: string) {
  return str.split(search).join(replacement)
}

const element = ref<HTMLElement | null>(null)

const formattedTextClass = computed(() => (
  props.noHTML
    ? undefined
    : [
      '[&_a]:text-comp-link-neut-def',
      '[&_b]:font-bold [&_strong]:font-bold',
      '[&_b[data-contact-uri]]:cursor-pointer [&_b[data-contact-uri]]:font-normal [&_b[data-contact-uri]]:text-surf-brand-basedarkblue-def',
      '[&_i]:italic [&_em]:italic',
      '[&_u]:underline',
      '[&_s]:line-through [&_strike]:line-through [&_del]:line-through',
      '[&_code]:rounded [&_code]:bg-bg-base-bg2 [&_code]:font-mono',
    ]
))

const showPlaceholder = computed(() => {
  const content = props.modelValue ?? ''
  const stripped = content
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<[^>]+>/g, '')
  // Spaces count as content — do not trim, otherwise placeholder stays over typed spaces
  return stripped.length === 0
})

const normalizeContent = (content: string) => {
  if (typeof props.maxLength !== 'number') return content
  return content.slice(0, props.maxLength)
}

function currentContent () {
  if (!element.value) return
  return props.noHTML
    ? element.value!.textContent
    : element.value!.innerHTML
}

function updateContent (newcontent: string) {
  if (!element.value) return
  const content = normalizeContent(newcontent ?? '')
  if (props.noHTML) {
    element.value!.textContent = content
  } else {
    // Keep <br> from line breaks; still strip colors/fonts/spans from clipboard.
    element.value!.innerHTML = sanitizeOutboundMessage(content)
  }
}

function update () {
  let content = normalizeContent(currentContent() ?? '')
  // Strip rich-text presentation (color/font) without rewriting mention HTML on every keystroke.
  if (!props.noHTML && /style\s*=|<\/?font\b|\bcolor\s*=/i.test(content)) {
    const sanitized = sanitizeOutboundMessage(content)
    if (sanitized !== content) {
      updateContent(sanitized)
      content = sanitized
    }
  } else if (content !== currentContent()) {
    updateContent(content)
  }
  emit('update:modelValue', content)
}

function onBeforeInput (event: Event) {
  if (typeof props.maxLength !== 'number') return
  if (!(event instanceof InputEvent)) return
  if (event.inputType.startsWith('delete')) return

  const selectedText = window.getSelection()?.toString() ?? ''
  const currentTextLength = currentContent()?.length ?? 0
  const remaining = props.maxLength - (currentTextLength - selectedText.length)

  if (remaining <= 0) {
    event.preventDefault()
  }
}

function onBlur (event: Event) {
  update()
  emit('blur', event)
}

const insertHtmlAtSelection = (html: string) => {
  window.document.execCommand('insertHTML', false, html)
}

/** Plain text only — avoids clipboard HTML colors (e.g. white-on-white from dark UI). */
function preparePlainText (raw: string | undefined | null) {
  let text = raw ?? ''
  if (props.noNL && text) {
    text = replaceAll(text, '\r\n', ' ')
    text = replaceAll(text, '\n', ' ')
    text = replaceAll(text, '\r', ' ')
  }
  const selectedText = window.getSelection()?.toString() ?? ''
  if (typeof props.maxLength === 'number') {
    const currentTextLength = currentContent()?.length ?? 0
    const remaining = props.maxLength - (currentTextLength - selectedText.length)
    text = remaining > 0 ? text.slice(0, remaining) : ''
  }
  return text
}

function insertPlainText (text: string) {
  const selection = window.getSelection()
  if (!selection?.rangeCount || !element.value) {
    window.document.execCommand('insertText', false, text)
    return
  }

  const range = selection.getRangeAt(0)
  if (!element.value.contains(range.commonAncestorContainer)) {
    window.document.execCommand('insertText', false, text)
    return
  }

  range.deleteContents()
  // Text node inherits editor color; execCommand('insertText') can keep sticky foreColor from rich clipboard.
  const textNode = document.createTextNode(text)
  range.insertNode(textNode)
  range.setStartAfter(textNode)
  range.collapse(true)
  selection.removeAllRanges()
  selection.addRange(range)
}

/** Jira/HTML clipboard sets sticky foreColor; next typed space then wraps text in <span style="color">. */
function clearStickyTypingFormat () {
  try {
    document.execCommand('removeFormat')
    document.execCommand('styleWithCSS', false, 'false')
    document.execCommand('foreColor', false, 'inherit')
  } catch {
    // ignore unsupported execCommand in some webviews
  }
}

function onPaste (event: ClipboardEvent) {
  event.preventDefault()

  if (!props.noHTML) {
    const html = event.clipboardData?.getData('text/html')
    if (html) {
      const normalizedHtml = normalizePasteHtml(html)
      if (normalizedHtml) {
        insertHtmlAtSelection(normalizedHtml)
        clearStickyTypingFormat()
        update()
        if (event.clipboardData?.files) {
          emit('filePicked', event.clipboardData.files)
        }
        return
      }
    }
  }

  insertPlainText(preparePlainText(event.clipboardData?.getData('text/plain')))
  clearStickyTypingFormat()
  update()
  if (event.clipboardData?.files) {
    emit('filePicked', event.clipboardData?.files)
  }
}

function onDragOver (event: DragEvent) {
  if (event.dataTransfer?.types.includes('text/plain') || event.dataTransfer?.types.includes('text/html')) {
    event.preventDefault()
  }
}

function onDrop (event: DragEvent) {
  const plain = event.dataTransfer?.getData('text/plain') ?? ''
  const html = event.dataTransfer?.getData('text/html') ?? ''
  const hasFiles = !!event.dataTransfer?.files?.length
  if (hasFiles && !plain && !html) return
  if (!plain && !html) return

  event.preventDefault()
  let text = plain
  if (!text && html) {
    const tmp = document.createElement('div')
    tmp.innerHTML = html
    text = tmp.textContent ?? ''
  }
  insertPlainText(preparePlainText(text))
  update()
}

function onKeyUpIsUp () {
  emit('editMyPreviousMessage')
}

function onKeyUpIsEsc () {
  emit('cancelEditingMyPreviousMessage')
}

onMounted(() => {
  updateContent(props.modelValue ?? '')
  if (element.value) {
    placeCaretAtEnd(element.value)
  }
})

watch(() => props.modelValue, (newval) => {
  if (newval === currentContent()) return

  const selectionSnapshot = snapshotContentEditableSelection(element.value)

  updateContent(newval ?? '')

  restoreContentEditableSelection(element.value, selectionSnapshot)
})

defineExpose({
  element,
  update,
})

</script>

<style scoped>
.is-placeholder-visible[data-placeholder]::before {
  content: attr(data-placeholder);
  color: var(--color-fg-base-fg22);
  pointer-events: none;
  float: left;
  height: 0;
}

/* Clipboard colors land on span/font. Do not force inherit on links, mentions, or code. */
.content-editable-input :deep(span),
.content-editable-input :deep(font) {
  color: inherit !important;
  background-color: transparent !important;
}
</style>
